import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  LayoutDashboard, Package, LogOut, Plus, Pencil, Trash2,
  X, Loader2, Upload, Lock, AlertCircle, ClipboardList
} from 'lucide-react';
import api from '../api';

const CATEGORIES = ['Oto Boya', 'Dış Cephe Boyası', 'İç Cephe Boyası', 'Astar', 'Tiner & Solvent', 'Diğer'];
const BRANDS = ['DYO', 'BASF', 'Sintaş', 'Diğer'];

const emptyForm = {
  name: '',
  description: '',
  price: '',
  category: CATEGORIES[0],
  brand: BRANDS[0],
  stock_status: true,
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem('adminToken') || '');

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setToken('');
  };

  return token
    ? <AdminPanel onLogout={handleLogout} />
    : <AdminLogin onSuccess={(t) => { localStorage.setItem('adminToken', t); setToken(t); }} />;
}

function AdminLogin({ onSuccess }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/api/auth/login', { email, password });
      onSuccess(data.token);
    } catch (err) {
      setError(err.response?.data?.error || 'E-posta veya şifre hatalı. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-red-900 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-2xl p-10 w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-red-700 rounded-xl flex items-center justify-center shadow-lg mb-4">
            <Lock className="text-white" size={28} />
          </div>
          <h1 className="text-2xl font-black text-gray-900">Yönetici Girişi</h1>
          <p className="text-sm text-gray-500 mt-1">Öncü Boya Admin Paneli</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-bold text-gray-900 mb-2">E-posta</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="admin@oncuboya.com"
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-red-600 focus:ring-2 focus:ring-red-100 transition font-medium"
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-900 mb-2">Şifre</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="••••••••"
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-red-600 focus:ring-2 focus:ring-red-100 transition font-medium"
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 bg-red-50 text-red-700 text-sm font-semibold p-3 rounded-lg">
              <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-red-600 to-red-700 text-white font-black py-3 rounded-lg hover:shadow-lg hover:scale-105 transition transform disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <><Loader2 className="animate-spin" size={20} /> Giriş yapılıyor...</> : 'Giriş Yap'}
          </button>
        </form>

        <Link to="/" className="block text-center text-sm text-gray-400 hover:text-red-600 transition mt-6">
          ← Ana sayfaya dön
        </Link>
      </div>
    </div>
  );
}

function AdminPanel({ onLogout }) {
  const [view, setView] = useState('dashboard');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  const fetchOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const { data } = await api.get('/api/orders');
      setOrders(data || []);
    } catch {
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  }, []);

  useEffect(() => { if (view === 'orders') fetchOrders(); }, [view, fetchOrders]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/api/products');
      setProducts(data || []);
    } catch (err) {
      setError('Ürünler yüklenirken bir hata oluştu.');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const openNewProductModal = () => {
    setEditingProduct(null);
    setModalOpen(true);
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setModalOpen(true);
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`"${product.name}" ürününü silmek istediğinize emin misiniz?`)) return;
    try {
      await api.delete(`/api/products/${product.id}`);
      fetchProducts();
    } catch (err) {
      window.alert('Ürün silinirken bir hata oluştu.');
    }
  };

  const totalCount = products.length;
  const inStockCount = products.filter(p => p.stock_status).length;
  const outOfStockCount = totalCount - inStockCount;
  const brandDistribution = BRANDS.map(brand => ({
    brand,
    count: products.filter(p => p.brand === brand).length,
  })).filter(b => b.count > 0);

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-gradient-to-b from-gray-900 to-gray-950 text-white flex flex-col flex-shrink-0">
        <div className="p-6 border-b border-white/10">
          <h1 className="text-xl font-black">ÖNCÜ BOYA</h1>
          <p className="text-xs text-gray-400 font-semibold">Yönetim Paneli</p>
        </div>
        <nav className="flex-1 p-4 space-y-2">
          <SidebarButton
            icon={<LayoutDashboard size={20} />}
            label="Dashboard"
            active={view === 'dashboard'}
            onClick={() => setView('dashboard')}
          />
          <SidebarButton
            icon={<Package size={20} />}
            label="Ürünler"
            active={view === 'products'}
            onClick={() => setView('products')}
          />
          <SidebarButton
            icon={<ClipboardList size={20} />}
            label="Siparişler"
            active={view === 'orders'}
            onClick={() => setView('orders')}
          />
        </nav>
        <div className="p-4 border-t border-white/10 space-y-2">
          <Link to="/" className="block text-sm text-gray-400 hover:text-white transition px-4 py-2">
            ← Siteyi Görüntüle
          </Link>
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-bold text-sm text-red-400 hover:bg-white/5 transition"
          >
            <LogOut size={20} /> Çıkış Yap
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 p-8 overflow-y-auto">
        {view === 'dashboard' && (
          <DashboardView
            totalCount={totalCount}
            inStockCount={inStockCount}
            outOfStockCount={outOfStockCount}
            brandDistribution={brandDistribution}
            loading={loading}
          />
        )}

        {view === 'products' && (
          <ProductsView
            products={products}
            loading={loading}
            error={error}
            onAdd={openNewProductModal}
            onEdit={openEditModal}
            onDelete={handleDelete}
          />
        )}

        {view === 'orders' && (
          <OrdersView orders={orders} loading={ordersLoading} onRefresh={fetchOrders} />
        )}
      </main>

      {modalOpen && (
        <ProductModal
          product={editingProduct}
          onClose={() => setModalOpen(false)}
          onSaved={() => { setModalOpen(false); fetchProducts(); }}
        />
      )}
    </div>
  );
}

function SidebarButton({ icon, label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-bold text-sm transition ${
        active ? 'bg-red-600 text-white shadow-lg' : 'text-gray-300 hover:bg-white/5'
      }`}
    >
      {icon} {label}
    </button>
  );
}

function DashboardView({ totalCount, inStockCount, outOfStockCount, brandDistribution, loading }) {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-3xl font-black text-gray-900">Dashboard</h2>
        <p className="text-gray-500 mt-1">Ürün kataloğunuza genel bakış</p>
      </div>

      {loading ? (
        <div className="flex items-center gap-3 text-gray-500">
          <Loader2 className="animate-spin text-red-600" size={24} />
          <span className="font-semibold">Veriler yükleniyor...</span>
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-3 gap-6">
            <StatCard label="Toplam Ürün" value={totalCount} color="from-gray-700 to-gray-900" />
            <StatCard label="Stokta Var" value={inStockCount} color="from-green-600 to-green-700" />
            <StatCard label="Stokta Yok" value={outOfStockCount} color="from-red-600 to-red-700" />
          </div>

          <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-6">
            <h3 className="text-lg font-black text-gray-900 mb-4">Marka Dağılımı</h3>
            {brandDistribution.length === 0 ? (
              <p className="text-gray-500 font-semibold">Henüz ürün eklenmemiş.</p>
            ) : (
              <div className="space-y-3">
                {brandDistribution.map(({ brand, count }) => (
                  <div key={brand} className="flex items-center gap-4">
                    <span className="w-20 text-sm font-bold text-gray-700">{brand}</span>
                    <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-red-600 to-red-400 rounded-full"
                        style={{ width: `${(count / totalCount) * 100}%` }}
                      />
                    </div>
                    <span className="w-8 text-right text-sm font-bold text-gray-900">{count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className={`bg-gradient-to-br ${color} text-white p-8 rounded-2xl shadow-xl`}>
      <p className="text-sm font-bold uppercase tracking-wide opacity-80">{label}</p>
      <p className="text-5xl font-black mt-3">{value}</p>
    </div>
  );
}

function ProductsView({ products, loading, error, onAdd, onEdit, onDelete }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-3xl font-black text-gray-900">Ürünler</h2>
          <p className="text-gray-500 mt-1">Ürün kataloğunuzu yönetin</p>
        </div>
        <button
          onClick={onAdd}
          className="flex items-center gap-2 bg-gradient-to-r from-red-600 to-red-700 text-white font-bold px-6 py-3 rounded-lg hover:shadow-lg hover:scale-105 transition transform"
        >
          <Plus size={20} /> Yeni Ürün Ekle
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center gap-3 text-gray-500 p-10">
            <Loader2 className="animate-spin text-red-600" size={24} />
            <span className="font-semibold">Ürünler yükleniyor...</span>
          </div>
        ) : error ? (
          <p className="text-red-600 font-bold p-10 text-center">{error}</p>
        ) : products.length === 0 ? (
          <p className="text-gray-500 font-semibold p-10 text-center">Henüz ürün eklenmemiş.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-900 text-white">
                <tr>
                  <th className="text-left font-bold uppercase tracking-wide px-6 py-4">ID</th>
                  <th className="text-left font-bold uppercase tracking-wide px-6 py-4">Foto</th>
                  <th className="text-left font-bold uppercase tracking-wide px-6 py-4">Ad</th>
                  <th className="text-left font-bold uppercase tracking-wide px-6 py-4">Marka</th>
                  <th className="text-left font-bold uppercase tracking-wide px-6 py-4">Kategori</th>
                  <th className="text-left font-bold uppercase tracking-wide px-6 py-4">Fiyat</th>
                  <th className="text-left font-bold uppercase tracking-wide px-6 py-4">Stok</th>
                  <th className="text-right font-bold uppercase tracking-wide px-6 py-4">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product, idx) => (
                  <tr key={product.id} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-red-50 transition`}>
                    <td className="px-6 py-4 text-gray-500 font-semibold">{product.id}</td>
                    <td className="px-6 py-4">
                      {product.image_url ? (
                        <img src={product.image_url} alt={product.name} className="w-12 h-12 object-cover rounded-lg border-2 border-gray-200" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center text-gray-300 text-xs font-bold">—</div>
                      )}
                    </td>
                    <td className="px-6 py-4 font-bold text-gray-900">{product.name}</td>
                    <td className="px-6 py-4 text-gray-600">{product.brand}</td>
                    <td className="px-6 py-4 text-gray-600">{product.category}</td>
                    <td className="px-6 py-4 font-bold text-red-600">
                      {product.price != null ? `₺${Number(product.price).toLocaleString('tr-TR')}` : '—'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-xs font-bold px-3 py-1 rounded-full ${product.stock_status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {product.stock_status ? 'Stokta Var' : 'Tükendi'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => onEdit(product)}
                          className="p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition"
                          aria-label="Düzenle"
                        >
                          <Pencil size={18} />
                        </button>
                        <button
                          onClick={() => onDelete(product)}
                          className="p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition"
                          aria-label="Sil"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function ProductModal({ product, onClose, onSaved }) {
  const isEditing = !!product;
  const [form, setForm] = useState(() => product
    ? {
        name: product.name || '',
        description: product.description || '',
        price: product.price != null ? String(product.price) : '',
        category: product.category || CATEGORIES[0],
        brand: product.brand || BRANDS[0],
        stock_status: !!product.stock_status,
      }
    : emptyForm
  );
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(product?.image_url || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('name', form.name);
      formData.append('description', form.description);
      formData.append('price', form.price === '' ? '' : form.price);
      formData.append('category', form.category);
      formData.append('brand', form.brand);
      formData.append('stock_status', form.stock_status);
      if (imageFile) formData.append('image', imageFile);

      if (isEditing) {
        await api.put(`/api/products/${product.id}`, formData);
      } else {
        await api.post('/api/products', formData);
      }

      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Ürün kaydedilirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-900/60 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h3 className="text-2xl font-black text-gray-900">
            {isEditing ? 'Ürünü Düzenle' : 'Yeni Ürün Ekle'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-red-600 transition">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="block text-sm font-bold text-gray-900 mb-2">Ürün Adı *</label>
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-red-600 focus:ring-2 focus:ring-red-100 transition font-medium"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-900 mb-2">Açıklama</label>
            <textarea
              rows="3"
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-red-600 focus:ring-2 focus:ring-red-100 transition font-medium resize-none"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-bold text-gray-900 mb-2">Fiyat</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-bold">₺</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.price}
                  onChange={(e) => handleChange('price', e.target.value)}
                  className="w-full pl-9 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-red-600 focus:ring-2 focus:ring-red-100 transition font-medium"
                />
              </div>
            </div>

            <div className="flex items-end pb-1">
              <label className="flex items-center gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={form.stock_status}
                  onChange={(e) => handleChange('stock_status', e.target.checked)}
                  className="w-5 h-5 accent-red-600"
                />
                <span className="font-bold text-gray-900">Stokta Var</span>
              </label>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-bold text-gray-900 mb-2">Kategori</label>
              <select
                value={form.category}
                onChange={(e) => handleChange('category', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-red-600 focus:ring-2 focus:ring-red-100 transition font-medium"
              >
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-900 mb-2">Marka</label>
              <select
                value={form.brand}
                onChange={(e) => handleChange('brand', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-red-600 focus:ring-2 focus:ring-red-100 transition font-medium"
              >
                {BRANDS.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-900 mb-2">Ürün Fotoğrafı</label>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 px-5 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-red-600 hover:text-red-600 transition font-bold text-gray-600">
                <Upload size={18} />
                Dosya Seç
                <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
              </label>
              {imagePreview && (
                <img src={imagePreview} alt="Önizleme" className="w-20 h-20 object-cover rounded-lg border-2 border-gray-200" />
              )}
            </div>
          </div>

          {error && (
            <div className="flex items-start gap-2 bg-red-50 text-red-700 text-sm font-semibold p-3 rounded-lg">
              <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 bg-gradient-to-r from-red-600 to-red-700 text-white font-black py-3 rounded-lg hover:shadow-lg hover:scale-105 transition transform disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? <><Loader2 className="animate-spin" size={20} /> Kaydediliyor...</> : (isEditing ? 'Güncelle' : 'Ürünü Ekle')}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 rounded-lg font-bold text-gray-600 border-2 border-gray-300 hover:border-gray-400 transition"
            >
              İptal
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const ORDER_STATUS_OPTIONS = ['new', 'preparing', 'shipped', 'completed', 'cancelled'];
const ORDER_STATUS_LABELS = {
  new: 'Yeni', preparing: 'Hazırlanıyor', shipped: 'Kargoda',
  completed: 'Tamamlandı', cancelled: 'İptal',
};
const PAYMENT_STATUS_LABELS = { pending: 'Bekliyor', paid: 'Ödendi', failed: 'Başarısız' };
const PAYMENT_STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  paid: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
};

function OrdersView({ orders, loading, onRefresh }) {
  const [selected, setSelected] = useState(null);

  const updateStatus = async (orderId, field, value) => {
    try {
      await api.put(`/api/orders/${orderId}/status`, { [field]: value });
      onRefresh();
    } catch {
      window.alert('Güncelleme başarısız.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-3 py-20 text-gray-500">
        <Loader2 className="animate-spin text-red-600" size={24} />
        <span className="font-semibold">Siparişler yükleniyor...</span>
      </div>
    );
  }

  if (selected) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <button onClick={() => setSelected(null)} className="text-gray-500 hover:text-gray-900 font-bold">
            ← Geri
          </button>
          <h2 className="text-2xl font-black text-gray-900">Sipariş Detayı</h2>
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl shadow border-2 border-gray-100 p-6 space-y-3">
            <h3 className="font-bold text-gray-900">Müşteri Bilgileri</h3>
            <p className="text-sm"><span className="font-semibold">Ad:</span> {selected.customer_name}</p>
            <p className="text-sm"><span className="font-semibold">Telefon:</span> {selected.customer_phone}</p>
            {selected.customer_email && <p className="text-sm"><span className="font-semibold">E-posta:</span> {selected.customer_email}</p>}
            <p className="text-sm"><span className="font-semibold">Adres:</span> {selected.customer_address}</p>
            {selected.notes && <p className="text-sm"><span className="font-semibold">Not:</span> {selected.notes}</p>}
          </div>

          <div className="bg-white rounded-2xl shadow border-2 border-gray-100 p-6 space-y-4">
            <h3 className="font-bold text-gray-900">Ödeme & Durum</h3>
            <p className="text-sm"><span className="font-semibold">Yöntem:</span> {selected.payment_method === 'havale' ? 'Havale / EFT' : 'Kredi Kartı'}</p>
            <p className="text-sm font-bold text-gray-900">Toplam: ₺{Number(selected.total_amount).toLocaleString('tr-TR')}</p>

            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1">Ödeme Durumu</label>
              <select
                value={selected.payment_status}
                onChange={(e) => { updateStatus(selected.id, 'payment_status', e.target.value); setSelected({...selected, payment_status: e.target.value}); }}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm font-medium"
              >
                {Object.entries(PAYMENT_STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1">Sipariş Durumu</label>
              <select
                value={selected.order_status}
                onChange={(e) => { updateStatus(selected.id, 'order_status', e.target.value); setSelected({...selected, order_status: e.target.value}); }}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm font-medium"
              >
                {ORDER_STATUS_OPTIONS.map(v => <option key={v} value={v}>{ORDER_STATUS_LABELS[v]}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow border-2 border-gray-100 p-6">
          <h3 className="font-bold text-gray-900 mb-4">Ürünler</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-2 font-bold text-gray-600">Ürün</th>
                <th className="text-center py-2 font-bold text-gray-600">Adet</th>
                <th className="text-right py-2 font-bold text-gray-600">Fiyat</th>
                <th className="text-right py-2 font-bold text-gray-600">Toplam</th>
              </tr>
            </thead>
            <tbody>
              {(selected.items || []).filter(i => i.id).map(item => (
                <tr key={item.id} className="border-b border-gray-50">
                  <td className="py-2">{item.product_name}</td>
                  <td className="py-2 text-center">{item.quantity}</td>
                  <td className="py-2 text-right">₺{Number(item.price).toLocaleString('tr-TR')}</td>
                  <td className="py-2 text-right font-bold">₺{(Number(item.price) * item.quantity).toLocaleString('tr-TR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-black text-gray-900">Siparişler</h2>
          <p className="text-gray-500 mt-1">{orders.length} sipariş</p>
        </div>
        <button onClick={onRefresh} className="text-sm font-bold text-red-600 hover:underline">Yenile</button>
      </div>

      {orders.length === 0 ? (
        <div className="text-center py-24 text-gray-400">
          <ClipboardList size={48} className="mx-auto mb-4 opacity-30" />
          <p className="font-bold">Henüz sipariş yok.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b-2 border-gray-100">
                <tr>
                  {['Sipariş No', 'Müşteri', 'Telefon', 'Tutar', 'Ödeme', 'Durum', 'Tarih', ''].map(h => (
                    <th key={h} className="px-4 py-3 text-left font-bold text-gray-600 text-xs uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {orders.map(order => (
                  <tr key={order.id} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-3 font-bold text-gray-900 whitespace-nowrap">{order.order_number}</td>
                    <td className="px-4 py-3 font-medium text-gray-800">{order.customer_name}</td>
                    <td className="px-4 py-3 text-gray-600">{order.customer_phone}</td>
                    <td className="px-4 py-3 font-bold text-gray-900">₺{Number(order.total_amount).toLocaleString('tr-TR')}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-bold ${PAYMENT_STATUS_COLORS[order.payment_status] || 'bg-gray-100 text-gray-700'}`}>
                        {PAYMENT_STATUS_LABELS[order.payment_status] || order.payment_status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-semibold text-gray-700 bg-gray-100 px-2 py-1 rounded-full">
                        {ORDER_STATUS_LABELS[order.order_status] || order.order_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">
                      {new Date(order.created_at).toLocaleDateString('tr-TR')}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setSelected(order)}
                        className="text-red-600 hover:text-red-800 font-bold text-xs hover:underline"
                      >
                        Detay →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
